<div class="content-container h-full flex flex-col justify-between">
  <div>
    @if (isPageLoading() || isLoading()) {
      <lib-overlay-loader />
    }

    <header class="py-lg flex items-center">
      <h1 class="text-xl leading-7 font-semibold">
        @if (editMode()) {
          {{ 'positions_form_edit_title' | transloco }}
        } @else {
          {{ 'positions_form_add_title' | transloco }}
        }
      </h1>
    </header>

    <form [formGroup]="positionFormGroup()" libScrollToError>
      <section>
        <h5
          class="text-neutral-900 font-semibold text-lg py-lg border-b border-neutral-200 my-2xl">
          {{ 'positions_form_position_info_section' | transloco }}
        </h5>

        <div
          class="grid grid-cols-[repeat(auto-fill,minmax(18.75rem,1fr))] max-w-[48.75rem] gap-lg">
          <!-- Position Name (EN + AR) -->
          <div>
            <div formGroupName="name">
              <dual-lang-input
                [requiredGroup]="true"
                [inputFormControlName]="'name'"
                [id]="'short_input_field_position_name_en'"
                type="text"
                name="name"
                [requiredEn]="false"
                [requiredAr]="false"
                [label]="'positions_form_position_name_label' | transloco"
                [placeholderEn]="
                  'positions_form_position_name_placeholder_en' | transloco
                "
                [placeholderAr]="
                  'positions_form_position_name_placeholder_ar' | transloco
                " />
            </div>
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="name" />
          </div>

          <!-- Position ID -->
          <div>
            <lib-input
              [inputFormControlName]="'identifier'"
              [id]="'short_input_field_position_identifier'"
              type="text"
              name="identifier"
              [label]="'positions_form_position_id_label' | transloco"
              [placeholder]="
                'positions_form_position_id_placeholder' | transloco
              " />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="identifier" />
          </div>

          <!-- Legal Entity -->
          <div>
            <lib-select
              [inputFormControlName]="'legal_entity_id'"
              id="drop_down_text_field_legal_entity"
              name="legal_entity_id"
              [required]="true"
              [label]="'positions_form_legal_entity_label' | transloco"
              [placeholder]="
                'positions_form_legal_entity_placeholder' | transloco
              "
              [options]="legalEntityOptions()"
              [selectedKeyValue]="'value'"
              [selectedKeyName]="'label'" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="legal_entity_id" />
          </div>

          <!-- Department -->
          <div>
            <lib-paginated-select
              [id]="'drop_down_text_field_department'"
              [inputFormControlName]="'department_id'"
              [name]="'department_id'"
              [required]="true"
              [label]="'positions_form_department_label' | transloco"
              [placeholder]="
                'positions_form_department_placeholder' | transloco
              "
              [classList]="['text-sm']"
              [apiUrl]="departmentApi"
              [selectedOption]="selectedDepartment"
              [noDataLabel]="'user_dropdown_input_no_data_label' | transloco" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="department_id" />
          </div>

          <!-- Job Title -->
          <div>
            <lib-paginated-select
              [id]="'drop_down_text_field_job_title'"
              [inputFormControlName]="'job_title_id'"
              [name]="'job_title_id'"
              [required]="true"
              [label]="'positions_form_job_title_label' | transloco"
              [placeholder]="'positions_form_job_title_placeholder' | transloco"
              [classList]="['text-sm']"
              [apiUrl]="jobTitleApi"
              [selectedOption]="selectedJobTitle"
              [noDataLabel]="'user_dropdown_input_no_data_label' | transloco"
              (changeOption)="onJobTitleSelect($event)" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="job_title_id" />
          </div>

          <!-- Reports To Position -->
          <div>
            <lib-paginated-select
              [id]="'drop_down_text_field_reports_to_position'"
              [inputFormControlName]="'reports_to_position_id'"
              [name]="'reports_to_position_id'"
              [label]="'positions_form_reports_to_position_label' | transloco"
              [placeholder]="
                'positions_form_reports_to_position_placeholder' | transloco
              "
              [classList]="['text-sm']"
              [apiUrl]="reportingPositionApi()"
              [selectedOption]="selectedReportsToPosition"
              [noDataLabel]="'user_dropdown_input_no_data_label' | transloco" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="reports_to_position_id" />
          </div>

          <!-- Position Status -->
          @if (!isPositionStatuesEnhancementEnabled()) {
            <div>
              <lib-select
                [inputFormControlName]="'position_status_option_id'"
                id="drop_down_text_field_position_status"
                name="position_status_option_id"
                [label]="'positions_form_position_status_label' | transloco"
                [placeholder]="
                  'positions_form_position_status_placeholder' | transloco
                "
                [options]="positionStatusOptions()"
                [selectedKeyValue]="'value'"
                [selectedKeyName]="'label'" />
              <lib-validation-message
                [lang]="'current_lang' | transloco"
                control="position_status_option_id" />
            </div>
          }

          <!-- FTE -->
          <div>
            <lib-input
              [inputFormControlName]="'fte'"
              [id]="'number_input_field_fte'"
              type="number"
              name="fte"
              [label]="'positions_form_fte_label' | transloco"
              [placeholder]="'positions_form_fte_placeholder' | transloco"
              [acceptDecimal]="true"
              [maxDecimalPlaces]="2"
              [min]="0"
              [max]="1" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="fte" />
          </div>

          <!-- Effective From -->
          <div>
            <lib-date-picker
              [inputFormControlName]="'effective_from'"
              [id]="'date_picker_effective_from'"
              [name]="'effective_from'"
              [placeholder]="
                'positions_form_effective_from_placeholder' | transloco
              "
              [label]="'positions_form_effective_from_label' | transloco" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="effective_from" />
          </div>

          <!-- Effective To -->
          <div>
            <lib-date-picker
              [inputFormControlName]="'effective_to'"
              [id]="'date_picker_effective_to'"
              [name]="'effective_to'"
              [placeholder]="
                'positions_form_effective_to_placeholder' | transloco
              "
              [label]="'positions_form_effective_to_label' | transloco" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="effective_to" />
          </div>

          <!-- Radford Grade (read-only derived) -->
          <div class="flex flex-col">
            <p class="view-mode-label">
              {{ 'positions_form_radford_grade_label' | transloco }}
            </p>
            <p class="view-mode-value break-word text-neutral-900">
              @if (radfordGrade()) {
                {{ radfordGrade() }}
              } @else {
                {{ 'details_not_found_label' | transloco }}
              }
            </p>
          </div>

          <!-- Career Track (read-only derived) -->
          <div class="flex flex-col">
            <p class="view-mode-label">
              {{ 'positions_form_career_track_label' | transloco }}
            </p>
            <p class="view-mode-value break-word text-neutral-900">
              @if (careerTrack()) {
                {{ careerTrack() }}
              } @else {
                {{ 'details_not_found_label' | transloco }}
              }
            </p>
          </div>
        </div>
      </section>
    </form>
  </div>

  <footer class="flex justify-end items-end pb-2xl">
    <div class="flex justify-end gap-lg mt-4xl">
      <a
        id="cancel"
        class="px-2xl py-sm rounded-md text-neutral-700 bg-white text-lg font-medium cursor-pointer"
        [routerLink]="[
          editMode()
            ? (ADMIN_ROUTES.POSITIONS.LIST | tenantPath: 'admin')
            : (ADMIN_ROUTES.POSITIONS.LIST | tenantPath: 'admin'),
        ]">
        {{ 'form_cancel_button' | transloco }}
      </a>

      <button
        [id]="
          editMode()
            ? 'positions_form_screen_save_edits_button'
            : 'positions_form_screen_add_button'
        "
        class="px-2xl py-sm rounded-md text-white bg-primary-500 text-lg font-medium border-none cursor-pointer"
        (click)="addEditPosition()"
        [class.opacity-30]="isLoading()"
        [disabled]="isLoading()">
        @if (editMode()) {
          {{ 'positions_form_save_edits_button' | transloco }}
        } @else {
          {{ 'positions_form_add_button' | transloco }}
        }
      </button>
    </div>
  </footer>
</div>
